import { Container } from "../../common/HtmlTags/Container/Container";
import { Paragraph } from "../../common/HtmlTags/Paragraph/Paragraph";
import { Img } from "../../common/HtmlTags/Img/Img";
import PopupModal, { PopupModalRef } from "../../common/Modal/Modal";
import LocationImg from "../../../assets/images/svgIcons/Location.svg";
import { LocationData } from "../LocationSelector/types";
import { Button } from "../../common/HtmlTags/Button/Button";
import { SHARE_LABELS } from "../../../utils/constants";
import MapLocationPicker from "../MapLocationPicker/MapLocationPicker";
import "./LocationModal.css";

interface LocationModalProps {
    locationModalRef: React.RefObject<PopupModalRef | null>;
    mapPickerModalRef: React.RefObject<PopupModalRef | null>;
    location: LocationData | null;
    isLoadingLocation: boolean;
    fetchUserLocation: () => void;
    openMapPicker: () => void;
    handleMapSelect: (location: LocationData) => void;
}

const LocationModal: React.FC<LocationModalProps> = ({
  locationModalRef,
  mapPickerModalRef,
  location,
  fetchUserLocation,
  isLoadingLocation,
  openMapPicker,
  handleMapSelect
}) => {
  return (
    <>
      <PopupModal
        modalTitle="Select Location"
        ref={locationModalRef}
        wrapperClass="location-selector-modal"
      >
        <Container className="location-selector-modal-content">
          <Container className="location-input-container">
            {location && (
              <Container className="location-display-container">
                <Img src={LocationImg} alt="location icon" />
                <Paragraph className="location-display-text">
                  {location?.address || SHARE_LABELS.SELECT_LOCATION}
                </Paragraph>
              </Container>
            )}

            <Container className="location-actions-wrap">
              <Button
                type="button"
                onClick={fetchUserLocation}
                disabled={isLoadingLocation}
                className="location-fetch-btn"
                palette="primary"
                size="medium"
                colorScheme="light"
                hasIcon={false}
              >
                {isLoadingLocation ? SHARE_LABELS.LOADING : SHARE_LABELS.USE_MY_LOCATION}
              </Button>
              <Button
                type="button"
                onClick={openMapPicker}
                className="location-pick-map-btn"
                palette="primary"
                size="medium"
                colorScheme="light"
                hasIcon={false}
              >
                {SHARE_LABELS.PICK_ON_MAP}
              </Button>
            </Container>
          </Container>
        </Container>
      </PopupModal>
      <PopupModal
        modalTitle="Pick Location"
        ref={mapPickerModalRef}
        wrapperClass="map-picker-modal"
      >
        <MapLocationPicker
          initialPosition={
            location ? { lat: location.lat, lng: location.lng } : undefined
          }
          onSelect={handleMapSelect}
        />
      </PopupModal>
    </>
  );
};

export default LocationModal;

/**
 * LocationModal component
 *
 * A dual-modal component that provides users with multiple ways to select their location.
 * Consists of a primary location selection modal and a secondary map picker modal for
 * interactive location selection.
 *
 * @component
 * @since 1.0.0
 *
 * @description
 * This component provides a comprehensive location selection interface with the following capabilities:
 * - Display current selected location with address
 * - Fetch user's current location using browser geolocation API
 * - Open an interactive map picker for manual location selection
 * - Handle loading states during location fetching
 * - Support for programmatic modal control via refs
 *
 * Modal Structure:
 * - **Primary Modal**: "Select Location" - Provides options to use current location or pick on map
 * - **Secondary Modal**: "Pick Location" - Interactive map interface for precise location selection
 *
 * @param {React.RefObject<PopupModalRef | null>} props.locationModalRef - Reference to the primary location
 *                                                                          selection modal. Used to programmatically
 *                                                                          open/close the "Select Location" modal.
 * @param {React.RefObject<PopupModalRef | null>} props.mapPickerModalRef - Reference to the map picker modal.
 *                                                                          Used to programmatically open/close the
 *                                                                          "Pick Location" modal with the interactive map.
 * @param {LocationData | null} props.location - The currently selected location data. When provided, displays
 *                                                the location address. When null, no location is shown.
 *                                                Contains lat, lng, and address properties.
 * @param {boolean} props.isLoadingLocation - Indicates whether the location fetching process is currently in progress.
 *                                           When true, the "Use My Location" button shows a loading state and is disabled.
 * @param {() => void} props.fetchUserLocation - Callback function triggered when the user clicks "Use My Location".
 *                                               Should initiate the browser geolocation API request to fetch the user's
 *                                               current coordinates and address.
 * @param {() => void} props.openMapPicker - Callback function triggered when the user clicks "Pick on Map".
 *                                          Should open the map picker modal (mapPickerModalRef) to allow interactive
 *                                          location selection.
 * @param {(location: LocationData) => void} props.handleMapSelect - Callback function triggered when a location is
 *                                                                  selected from the map picker. Receives the selected
 *                                                                  LocationData object with lat, lng, and address.
 *
 * @returns {JSX.Element} Dual modal structure with location selection options and map picker interface.
 *
 * @example
 * // Basic usage with location selection
 * ```tsx
 * const locationModalRef = useRef<PopupModalRef>(null);
 * const mapPickerModalRef = useRef<PopupModalRef>(null);
 * const [location, setLocation] = useState<LocationData | null>(null);
 * const [isLoadingLocation, setIsLoadingLocation] = useState(false);
 *
 * const handleFetchLocation = async () => {
 *   setIsLoadingLocation(true);
 *   try {
 *     const position = await getCurrentPosition();
 *     const address = await reverseGeocode(position.coords.latitude, position.coords.longitude);
 *     setLocation({
 *       lat: position.coords.latitude,
 *       lng: position.coords.longitude,
 *       address: address,
 *     });
 *   } finally {
 *     setIsLoadingLocation(false);
 *   }
 * };
 *
 * const handleOpenMapPicker = () => {
 *   locationModalRef.current?.close();
 *   mapPickerModalRef.current?.open();
 * };
 *
 * const handleMapSelect = (selectedLocation: LocationData) => {
 *   setLocation(selectedLocation);
 *   mapPickerModalRef.current?.close();
 * };
 *
 * return (
 *   <LocationModal
 *     locationModalRef={locationModalRef}
 *     mapPickerModalRef={mapPickerModalRef}
 *     location={location}
 *     isLoadingLocation={isLoadingLocation}
 *     fetchUserLocation={handleFetchLocation}
 *     openMapPicker={handleOpenMapPicker}
 *     handleMapSelect={handleMapSelect}
 *   />
 * );
 * ```
 *
 * @example
 * // Opening the modal programmatically
 * ```tsx
 * const locationModalRef = useRef<PopupModalRef>(null);
 *
 * const openLocationModal = () => {
 *   locationModalRef.current?.open();
 * };
 *
 * return (
 *   <>
 *     <Button onClick={openLocationModal}>Select Location</Button>
 *     <LocationModal
 *       locationModalRef={locationModalRef}
 *       mapPickerModalRef={mapPickerModalRef}
 *       location={null}
 *       isLoadingLocation={false}
 *       fetchUserLocation={handleFetchLocation}
 *       openMapPicker={handleOpenMapPicker}
 *       handleMapSelect={handleMapSelect}
 *     />
 *   </>
 * );
 * ```
 */
